// Angular
import {inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {BehaviorSubject, Observable} from 'rxjs';
import {Transaction} from '~local/app/shared/objects/Transaction';
import {SocketNamespaceServiceBase} from '~local/app/core/socket/SocketNamespaceServiceBase';
import {filter, map} from 'rxjs/operators';
import {AuthenticationService} from '~local/app/core/services/authentication.service';
import {TransactionHistory} from '~local/app/shared/types/kycTierLimit';
import {PaginateResults} from '~shared/types/base-types';
import {TransactionFiltersParams} from '~shared/types/transaction-types';

@Injectable()
export class TransactionService extends SocketNamespaceServiceBase {
    protected readonly authenticationService = inject(AuthenticationService);
    protected readonly httpClient = inject(HttpClient);
    protected readonly _transactions$ = new BehaviorSubject<Array<Transaction>>([]);

    public constructor() {
        super('transactions');
        this.authenticationService.credentials$.pipe(
            filter($ => $ === null),
        ).subscribe(() => {
            if (this._transactions$.value !== null) {
                this._transactions$.next(null);
            }
        });
    }

    private getData() {
        this.socket.once('dataR', ($data: Array<Transaction>) => {
            this._transactions$.next($data);
        });
        this.socket.emit('data');
    }

    protected addTransaction($transaction: Transaction, $append: boolean = false): void {
        if ($append) {
            this._transactions$.next([
                ...this._transactions$.value,
                $transaction,
            ]);
        } else {
            this._transactions$.next([
                $transaction,
                ...this._transactions$.value,
            ]);
        }
    }

    private updateTransaction($transaction: Transaction): void {
        const existingTransaction = this._transactions$.value.find($ => $.id === $transaction.id);
        const existingTransactionIndex = this._transactions$.value.indexOf(existingTransaction);

        this._transactions$.next([
            ...this._transactions$.value.slice(0, existingTransactionIndex),
            {
                ...existingTransaction,
                ...$transaction,
            },
            ...this._transactions$.value.slice(existingTransactionIndex + 1),
        ]);
    }

    protected onConnect() {
        this.getData();
    }

    /**
     * @deprecated
     */
    public get all(): Transaction[] {
        return this._transactions$.value;
    }

    public transaction($id: number): Transaction {
        return this._transactions$.value.find($ => $.id === $id);
    }

    public getByAccount(id: number): Array<Transaction> | null {
        return this._transactions$.value.filter($ => $.accountFromId === id || $.accountToId === id);
    }

    // protected listeners
    protected initListeners() {
        this.socket.on('update', ($transactionId: number, $changed: Transaction) => {
            const existingTransaction = this._transactions$.value.find($ => $.id === $transactionId);
            if (!existingTransaction) {
                this.addTransaction($changed, true);
                return;
            } else {
                this.updateTransaction($changed);
            }
        });

        this.socket.on('new', ($transactionId: number, $transaction: Transaction) => {
            this.addTransaction($transaction);
        });
    }

    public get transactions$(): Observable<Array<Transaction>> {
        return this._transactions$.pipe(
            filter($ => $ !== null),
        );
    }

    /** API ROUTES */
    public getTransactionList($filterParams: TransactionFiltersParams = {order: {createdDate: 'DESC'}}): Observable<PaginateResults<Transaction>> {
        // FIXME remove "list1" from |url| and |api| when will be delete old transaction repository search method
        return this.httpClient.post<PaginateResults<Transaction>>('/transactions/list1', $filterParams);
    }

    public getTransactionHistory($id: number): Observable<Array<TransactionHistory>> {
        return this.httpClient.get<Array<TransactionHistory>>('/transactions/info/history/' + $id);
    }

    // FIXME rafactor ALL TRANSACTIONS TYPEs
    public getTransaction($number: string): Observable<any> {
        return this.httpClient.get<any>('/transactions/detail/' + $number).pipe(map($ => {
            return {...$, iban: $.detail?.iban ?? null, beneficiary: $.detail?.beneficiary ?? null};
        }));
    }

    public loadPdf($id: number | string): Observable<Blob> {
        return this.httpClient.get('/reports/transaction/pdf/' + $id, {responseType: 'blob'});
    }
}
